import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { generateTemporaryId } from "../../u2000Core/tools/U2000IdUtils";
import { NS, U3139ROLES } from "../U3139";
import { useU3139Context } from "../U3139Context";
import type { U3139FormNavigationState } from "../U3139InitCreateMinuteDialogContext";
import {
  useU3139FormDeleteHandlers,
  useU3139GetHandlers,
  useU3139SaveHandlers,
  useU3139SearchHandlers,
} from "../U3139UseApiHandlers";
import {
  doGetAuditsData,
  emptyMinutes,
  luHandleAppiError,
  mapInitPreviewToFormRow,
  u3139SyncSecondaryWorkNaturesFromCatalog,
  u3139SyncWorkNatureFromCatalog,
  validateMinutesForm,
  type U3139MinutesFormRow,
} from "../U3139Service";
import U3139MinutesBody from "./form/u3139Minutes/U3139MinutesBody";
import U3139DetailLotsBody, { type U3139MinutesFormExtendedHandlers } from "./form/u3139Minutes/u3139DetailLotsTab/U3139DetailLotsBody";
import { U3139MinutesFormToolBar } from "./form/u3139Minutes/U3139MinutesFormToolBar";
import U3139MinutesDeleteDlg from "./common/U3139MinutesDeleteDlg";

const U3139MinutesForm: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { initLU, luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3139Context();
  const [formState, formStateDispatch] = useReducer<U2000FormState, [action: { type: string; data?: unknown }]>(u2000FormState, u2000IntFormState);
  const [activeTab, setActiveTab] = useState<string | null>("tabDetailLots");

  const initPreview = (location.state as U3139FormNavigationState | null)?.initPreview;
  const initPreviewKey = useMemo(() => (initPreview ? JSON.stringify(initPreview) : ""), [initPreview]);
  const projectLocked = id === "new" && Boolean(initPreview?.project?.id);
  const listTitle = t("title", { ns: NS });

  const navigateToRecordId = useCallback(
    (recordId: string) => {
      const targetId = String(recordId);
      if (!targetId || id === targetId) return;
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, targetId);
      navigate(newPath, { replace: true });
    },
    [id, location.pathname, navigate],
  );

  const handleSearch = useU3139SearchHandlers<U3139MinutesFormRow>({
    onSuccess: (searchResult: U2000SearchResult<U3139MinutesFormRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      const row = searchResult.data[idx];
      if (row?.id) navigateToRecordId(String(row.id));
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU3139GetHandlers<U3139MinutesFormRow & { id: string }>({
    onSuccess: (minute) => {
      const synced = {
        ...minute,
        workNature: u3139SyncWorkNatureFromCatalog(minute.workNature, initLU.workNatures),
        secondaryWorkNatures: u3139SyncSecondaryWorkNaturesFromCatalog(
          minute.secondaryWorkNatures,
          initLU.workNatures,
        ),
      };
      gridStateHandlers.setDataById(synced.id, synced);
      minutesExtended.setData(synced);
      const recordId = String(minute.id);
      if (!location.pathname.endsWith(recordId)) {
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, recordId);
        navigate(newPath, { replace: true });
      }
      setLogicalUnitBreadcrumb([
        { href: "/U3139", label: listTitle },
        { label: `${minute.minuteNumber ?? ""} — ${minute.clientName ?? ""}`.trim() },
      ]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        minutesExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU3139SaveHandlers({
    onSuccess: (minute, editMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), minute]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(minute.id));
        navigate(newPath, { replace: true });
      } else {
        gridStateHandlers.setDataById(String(minute.id), minute);
      }
      minutesExtended.afterSaveData(minute);
      toast.success(t("msgMinuteSaved", { ns: NS }));
      if (
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY
      ) {
        if (luStateHandlers.getActionOriginFrom() === "/U3139") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        minutesExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU3139FormDeleteHandlers({
    getSelectedRows: () => [minutesExtended.getData()],
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: (row as U3139MinutesFormRow).minuteNumber ?? row.id, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((r) => r.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(
        gridStateHandlers.getCurrentIdx(),
        newData,
        totalRows,
      );
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        navigateToRecordId(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3139", { replace: true });
      }
    },
    onError: (errors: { row: U3139MinutesFormRow; error: unknown }[]) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const minutesBL: U2000FormSBusinessHandlers<U3139MinutesFormRow> = {
    newRecord: () => emptyMinutes(),
    validateForm: (row) => validateMinutesForm(row, t, NS),
    saveForme: async (row, editMode) => {
      const errors = validateMinutesForm(row, t, NS);
      if (Object.keys(errors).length > 0) {
        minutesExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = minutesExtended.getOldData();
      if (old) minutesExtended.setData({ ...old });
      if (luStateHandlers.getActionOriginFrom() === "/U3139") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row?.id) navigateToRecordId(String(row.id));
    },
    onChangePageForm: (page, direction) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => navigate("/U3139"),
  };

  const minutesFormHandlers = useU2000FormStateHandlers<U3139MinutesFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: minutesBL,
  });

  const minutesExtended: U3139MinutesFormExtendedHandlers = {
    ...minutesFormHandlers,
    addNewMinute: () => minutesFormHandlers.setEditMode(U2000FormStateEnumEditMode.ADD),
    modifyMinute: () => minutesFormHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT),
    deleteMinute: () => handleDelete.handleDelete(),
  };

  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      if (initPreview) {
        const mapped = mapInitPreviewToFormRow(initPreview);
        mapped.detailLots = (mapped.detailLots ?? []).map((lot) => ({
          ...lot,
          id: lot.id?.trim() || generateTemporaryId(),
        }));
        minutesExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
        minutesExtended.setData(mapped);
        const p = mapped.project;
        const crumb = p?.projetNumber ? `${p.projetNumber} — ${p.projetName ?? ""}` : t("lblNewMinute", { ns: NS });
        setLogicalUnitBreadcrumb([{ href: "/U3139", label: listTitle }, { label: crumb }]);
      } else {
        minutesExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
        minutesExtended.setData(emptyMinutes());
        setLogicalUnitBreadcrumb([{ href: "/U3139", label: listTitle }, { label: t("lblNewMinute", { ns: NS }) }]);
      }
    } else {
      handleGet.getData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initPreviewKey]);

  const formTabs = [
    {
      id: "tabDetailLots",
      label: t("tabDetailLots", { ns: NS }),
      content: <U3139DetailLotsBody minutesFormStateHandlers={minutesExtended} />,
    },
    hasRole(U3139ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: (
        <U2000AuditPanelComponent id={minutesExtended.getData()?.id ?? ""} doGetAuditsData={doGetAuditsData} toastName="U3139MinutesAudit" />
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <U3139MinutesDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3139MinutesFormToolBar formStateHandlers={minutesExtended} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers as any}
        formStateHandlers={minutesExtended as any}
      >
        <U3139MinutesBody formStateHandlers={minutesExtended as any} t={t} workNatures={initLU.workNatures ?? []} projectLocked={projectLocked} />
        <U2000Tab
          className="mt-4"
          tabs={formTabs as { id: string; label: string; content: React.ReactNode }[]}
          defaultTab="tabDetailLots"
          activeTab={activeTab ?? "tabDetailLots"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3139MinutesForm;
